import { DateFormatter, parseDate } from "@internationalized/date";
import { useSuspenseQuery } from "@tanstack/react-query";

import type { Tournament, Venue } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";
import { getDefaultTimeZone } from "@/lib/dates";
import {
  getGenderDisplay,
  getLevelDisplay,
  getPlayerLevel,
  getTournamentDivisionDisplay,
} from "@/utils/tournament";
import { isDefined } from "@/utils/types";

export {
  getGenderDisplay,
  getLevelDisplay,
  getPlayerLevel,
  getTournamentDivisionDisplay,
};

const dateFormatter = new DateFormatter("EN-US", {
  dateStyle: "short",
});

export function getTournamentDisplay(
  {
    name,
    date,
    venue,
  }: Pick<Tournament, "name" | "date"> & {
    venue: Pick<Venue, "name" | "city" | "slug">;
  },
  short = false,
) {
  return [
    name?.trim(),
    dateFormatter.format(parseDate(date).toDate(getDefaultTimeZone())),
    short ? venue.slug : `${venue.name}, ${venue.city}`,
  ]
    .filter(isDefined)
    .join(", ");
}

export function useTournamentDivision(
  tournamentId: number,
  tournamentDivisionId: number,
) {
  const trpc = useTRPC();

  return useSuspenseQuery({
    ...trpc.tournaments.get.queryOptions({ id: tournamentId }),
    select: (data) =>
      data?.tournamentDivisions.find(({ id }) => id === tournamentDivisionId),
  });
}

export function useTournamentDivisionName(
  tournamentId: number,
  tournamentDivisionId: number,
) {
  const { data: division } = useTournamentDivision(
    tournamentId,
    tournamentDivisionId,
  );

  if (!division) {
    return null;
  }

  return getTournamentDivisionDisplay(division);
}
